import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FiHome, FiArrowLeft, FiCompass, FiHelpCircle, FiMail, FiBookOpen } from "react-icons/fi";
import { setTitle } from "../../../utils/commonfunction";

function Page404({ homePath = "/" }) {
  useEffect(() => {
    setTitle("404 - Page Not Found | Astro Adi");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const popularLinks = [
    { name: "About AstroAdi", path: "/about", icon: <FiCompass className="text-yellow-500" /> },
    { name: "FAQ", path: "/faq", icon: <FiHelpCircle className="text-yellow-500" /> },
    { name: "Privacy Policy", path: "/privacy", icon: <FiBookOpen className="text-yellow-500" /> },
    { name: "Terms & Conditions", path: "/terms", icon: <FiBookOpen className="text-yellow-500" /> },
    { name: "Contact Us", path: "/contact", icon: <FiMail className="text-yellow-500" /> },
  ];

  return (
    <div className="page-404-wrapper bg-white">
      {/* ===== Hero Banner Section ===== */}
      <section className="relative bg-[url('../../images/banner.png')] bg-cover bg-center !pt-[78px] md:!pt-[88px] !pb-[16px] md:!pb-[22px] overflow-hidden text-center">
        {/* Dark overlay for rich contrast */}
        <div className="absolute inset-0 bg-black/70 z-[1]"></div>

        <div className="container relative z-[2] mx-auto px-4">
          <div className="max-w-[700px] mx-auto flex flex-col items-center gap-1.5">
            {/* Red / Amber Error Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-red-500/15 border border-red-400/40 text-red-400 text-[11px] md:text-xs font-semibold tracking-wider uppercase shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></span>
              ERROR 404
            </div>

            {/* Page Title */}
            <h1 className="web-h1 text-white text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight m-0 drop-shadow-md">
              Page Not Found
            </h1>

            {/* Breadcrumb Navigation */}
            <nav className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1 rounded-full border border-white/10 text-xs text-gray-300 mt-0.5">
              <Link
                to="/"
                className="text-gray-300 hover:text-yellow-400 no-underline transition-colors flex items-center gap-1"
              >
                <FiHome className="text-xs" />
                <span>Home</span>
              </Link>
              <span className="text-gray-500">/</span>
              <span className="text-red-400 font-medium">404 Not Found</span>
            </nav>
          </div>
        </div>
      </section>

      {/* ===== 404 Body Content Section ===== */}
      <section className="pt-4 md:pt-6 pb-12 md:pb-16 bg-gray-50/70 relative overflow-hidden">
        {/* Decorative background shapes */}
        <div className="absolute top-[10%] right-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape1.png" alt="" />
        </div>
        <div className="absolute bottom-[10%] left-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape2.png" alt="" />
        </div>

        <div className="container max-w-4xl mx-auto px-4 sm:px-6 relative z-[2]">
          <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 shadow-xl shadow-gray-200/60 border border-gray-100 text-center">
            {/* Giant Glowing 404 Text */}
            <div className="relative inline-block mb-4 select-none">
              <span className="text-6xl sm:text-7xl md:text-8xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 drop-shadow-md">
                404
              </span>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"></div>
            </div>

            <h2 className="web-h2 text-gray-900 text-2xl sm:text-3xl font-bold mb-3 tracking-tight">
              Lost in the Cosmic Void?
            </h2>

            <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-8">
              The astrological coordinates you are searching for do not exist or may have shifted. Don't worry, the stars are always here to guide you back home.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <Link
                to={homePath}
                className="custom-btn custom-btn-primary !px-8 !py-3.5 flex items-center gap-2 text-base font-semibold shadow-lg shadow-yellow-500/20 !no-underline"
              >
                <FiArrowLeft />
                <span>Back to Home</span>
              </Link>
              <Link
                to="/contact"
                className="custom-btn custom-btn-white !px-8 !py-3.5 flex items-center gap-2 text-base font-semibold border border-gray-300 !no-underline hover:border-yellow-400 transition-colors"
              >
                <FiMail />
                <span>Contact Support</span>
              </Link>
            </div>

            {/* Helpful Links Box */}
            <div className="border-t border-gray-100 pt-8 max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-wider text-gray-400 font-bold block mb-4">
                Popular Destinations
              </span>
              <div className="flex flex-wrap justify-center gap-2.5">
                {popularLinks.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.path}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 border border-gray-200/80 text-gray-700 hover:text-yellow-600 hover:border-yellow-300 hover:bg-amber-50/30 transition-all text-xs sm:text-sm font-medium no-underline"
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Page404;
